import { useState, useRef } from "react";
import DynamicIcon from "../DynamicIcon";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    rectSortingStrategy,
    useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function SortableMediaItem({ item, index, onRemove, onSetCover }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: item._tempId || item.url });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 10 : 'auto'
    };

    const isImage = item.kind && item.kind.startsWith('image/');
    const isVideo = item.kind && item.kind.startsWith('video/');

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`relative group border-2 rounded-lg overflow-hidden ${item.cover ? 'border-yellow-400' : 'border-gray-200'} bg-white`}
        >
            {/* Cover badge */}
            {item.cover && (
                <div className="absolute top-1 left-1 z-10 bg-yellow-400 text-yellow-900 text-xs font-semibold px-2 py-0.5 rounded">
                    Copertina
                </div>
            )}

            {/* Thumbnail */}
            <div className="w-full h-32 bg-gray-100 flex items-center justify-center cursor-grab active:cursor-grabbing" {...attributes} {...listeners}>
                {isImage && (
                    <img
                        src={item.url}
                        alt={item.fileName || 'Media'}
                        className="w-full h-full object-contain"
                        draggable={false}
                    />
                )}
                {isVideo && (
                    <div className="flex flex-col items-center text-gray-400">
                        <DynamicIcon iconClass="fa-solid fa-video text-2xl" />
                        <span className="text-xs mt-1">Video</span>
                    </div>
                )}
                {!isImage && !isVideo && (
                    <div className="flex flex-col items-center text-gray-400">
                        <DynamicIcon iconClass="fa-solid fa-file text-2xl" />
                        <span className="text-xs mt-1">{item.kind || 'File'}</span>
                    </div>
                )}
            </div>

            {/* Filename */}
            <div className="px-2 py-1 text-xs text-gray-500 truncate">
                {item.fileName || 'File'}
            </div>

            {/* Actions overlay */}
            <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {/* Set cover */}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onSetCover(index); }}
                    className={`p-1.5 rounded-full shadow-sm text-xs ${item.cover ? 'bg-yellow-400 text-yellow-900' : 'bg-white text-gray-500 hover:text-yellow-500'}`}
                    title={item.cover ? 'Copertina attiva' : 'Imposta come copertina'}
                >
                    <DynamicIcon iconClass="fa-solid fa-star" />
                </button>

                {/* Remove */}
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onRemove(index); }}
                    className="p-1.5 rounded-full shadow-sm bg-white text-gray-500 hover:text-red-500 text-xs"
                    title="Rimuovi"
                >
                    <DynamicIcon iconClass="fa-solid fa-times" />
                </button>
            </div>
        </div>
    );
}

export default function MediaGallery({ media = [], onChange }) {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const token = localStorage.getItem("installamelo-auth-token") ?? "";

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        setUploading(true);

        try {
            const uploaded = [];
            for (const file of files) {
                const uploadFormData = new FormData();
                uploadFormData.append('file', file);
                uploadFormData.append('path', 'products/media');
                uploadFormData.append('uploadTitle', file.name);

                const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/files/upload`, {
                    method: 'POST',
                    headers: { 'auth-token': token },
                    body: uploadFormData
                });

                if (!res.ok) throw new Error('Errore durante l\'upload');

                const data = await res.json();
                uploaded.push({
                    fileName: data.fileName,
                    url: data.urlFile,
                    kind: data.kind,
                    order: media.length + uploaded.length,
                    cover: media.length === 0 && uploaded.length === 0,
                    _tempId: `temp_${Date.now()}_${uploaded.length}`
                });
            }

            onChange([...media, ...uploaded]);
        } catch (err) {
            console.error('Errore upload media:', err);
            alert('Errore durante il caricamento del file');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemove = (index) => {
        const updated = media.filter((_, i) => i !== index);
        // Se abbiamo rimosso la copertina, assegna la copertina al primo elemento
        if (media[index]?.cover && updated.length > 0) {
            updated[0] = { ...updated[0], cover: true };
        }
        // Ricalcola ordini
        onChange(updated.map((m, i) => ({ ...m, order: i })));
    };

    const handleSetCover = (index) => {
        const updated = media.map((m, i) => ({
            ...m,
            cover: i === index
        }));
        onChange(updated);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const ids = media.map(m => m._tempId || m.url);
        const oldIndex = ids.indexOf(active.id);
        const newIndex = ids.indexOf(over.id);
        const reordered = arrayMove(media, oldIndex, newIndex).map((m, i) => ({ ...m, order: i }));
        onChange(reordered);
    };

    const sortableIds = media.map(m => m._tempId || m.url);

    return (
        <div>
            {/* Upload button */}
            <div className="mb-3">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleUpload}
                    className="hidden"
                    id="media-upload-input"
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50"
                >
                    {uploading ? (
                        <>
                            <DynamicIcon iconClass="fa-solid fa-spinner fa-spin mr-2" />
                            Caricamento...
                        </>
                    ) : (
                        <>
                            <DynamicIcon iconClass="fa-solid fa-upload mr-2" />
                            Carica Media
                        </>
                    )}
                </button>
                <span className="text-xs text-gray-400 ml-3">
                    Immagini e video. Trascina per riordinare.
                </span>
            </div>

            {/* Media grid */}
            {media.length > 0 ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={sortableIds} strategy={rectSortingStrategy}>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {media.map((item, index) => (
                                <SortableMediaItem
                                    key={item._tempId || item.url}
                                    item={item}
                                    index={index}
                                    onRemove={handleRemove}
                                    onSetCover={handleSetCover}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            ) : (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                    <DynamicIcon iconClass="fa-solid fa-image text-4xl text-gray-300 mb-3" />
                    <p className="text-gray-400 text-sm">Nessun media. Clicca "Carica Media" per aggiungere immagini o video.</p>
                </div>
            )}
        </div>
    );
}
